import "./index.scss";

import { Box, CircularProgress, Grid, Modal, Stack, Tab, Tabs } from "@mui/material";
import { ReactComponent as IconMember } from "assets/images/side-menu/members-off.svg";
import { ReactComponent as IconWorkingOutOff } from "assets/images/side-menu/members-working-out-off.svg";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import { ReactComponent as UncheckedCircleIcon } from "assets/images/toggle-checkboxes/circle/off-normal.svg";
import { ReactComponent as CheckedCircleIcon } from "assets/images/toggle-checkboxes/circle/on-normal.svg";
import SearchBar from "components/SearchBar";
import { MSG_OPERATION_FAILED } from "constants/index";
import CustomSelect from "containers/Members/Select";
import { TPagination } from "modules/api/type";
import { getMemberList } from "modules/member/memberServices";
import { memo, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

const CopyFromMemberModal = (props: { open: boolean; onClose: any }) => {
  const { open, onClose } = props;
  const [loading, setLoading] = useState(false);
  const [showMyMembersNotAll, setShowMyMembersNotAll] = useState(true);
  const [searchStr, setSearchStr] = useState("");
  const [options, setOptions] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const [pagination, setPagination] = useState<TPagination>();
  const [params, setParams] = useState({
    limit: 10,
    my_members_only: true,
    page: 1,
    search: "",
    sort_by: "LAST_NAME",
    sort_order: "ASC",
    status: "",
    workout_plan_availability: "WITH_WOP",
  });

  useEffect(() => {
    if (open && (!pagination || filterOptions.length < pagination?.total_records)) {
      setLoading(true);
      getMemberList(params)
        .then((res) => {
          if (res.success) {
            const data = searchStr ? res.data : [...options, ...res.data];
            setOptions(data);
            setFilterOptions(data);
            setPagination(res.pagination);
          } else {
            throw new Error();
          }
        })
        .catch((e) => {
          console.log(e);
          toast(MSG_OPERATION_FAILED, { type: "error" });
        })
        .finally(() => setLoading(false));
    }
  }, [open, params]);

  //#region event handlers
  const handleCloseModal = useCallback(() => {
    onClose();
  }, []);

  const handleChangeMembersTab = (event: React.SyntheticEvent, newValue: boolean) => {
    setOptions([]);
    setFilterOptions([]);
    setPagination(null);
    setShowMyMembersNotAll(newValue);
    setParams({ ...params, my_members_only: newValue, page: 1 });
  };

  const handleSearch = (str: string) => {
    setOptions([]);
    setFilterOptions([]);
    setPagination(null);
    setParams({ ...params, page: 1, search: str });
    setSearchStr(str);
  };

  const handleStatusChange = (values) => {
    setOptions([]);
    setFilterOptions([]);
    setPagination(null);
    setParams({ ...params, page: 1, status: values.join(",") });
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && pagination) {
      if (params.page < pagination.total_pages) {
        setParams({ ...params, page: params.page + 1 });
      }
    }
  };
  //#endregion event handlers

  return (
    <Modal
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      open={open}
      onClose={handleCloseModal}
    >
      <Stack className="personal-details-modal-container copy-modal">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="40px"
          className="header"
        >
          <h1>Copy from Member</h1>
          <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
        </Stack>
        <Tabs className="spreadTabs" value={showMyMembersNotAll} onChange={handleChangeMembersTab}>
          <Tab label="My Members" value={true} />
          <Tab label="All Members" value={false} />
        </Tabs>
        <Box className={`searchable-list-container`}>
          <Box className="header">
            <Stack direction={"row"} gap={1}>
              <SearchBar
                className="search-bar"
                value={searchStr}
                onSearch={handleSearch}
                isAsync={true}
              />
              <CustomSelect handler={handleStatusChange} />
            </Stack>
          </Box>
          <Box className="list" onScroll={handleScroll}>
            {filterOptions.map((item, index) => (
              <button
                className={`checkable-button`}
                key={`copyFromMember${index}`}
                onClick={() => onClose(item)}
              >
                <Grid container spacing={8}>
                  <Grid item xs={1}>
                    <IconMember className="icon-dark" />
                  </Grid>
                  <Grid item xs={10}>
                    {item.last_name} {item.first_name}
                  </Grid>
                  <Grid item xs={1} className="check">
                    <CheckedCircleIcon className="showOnHover" />
                    <UncheckedCircleIcon className="showOnMouseOut" />
                  </Grid>
                  <Grid item xs={1}></Grid>
                  <Grid item xs={11} className="memberId">
                    <span>Member</span> {item.id}
                  </Grid>
                  <Grid item xs={1}>
                    <IconWorkingOutOff className="icon-dark size18" />
                  </Grid>
                  <Grid item xs={11}>
                    {item.workouts.name}
                  </Grid>
                  <Grid item xs={1}></Grid>
                  <Grid item xs={11}>
                    {item.workouts.has_cardio_workouts && (
                      <span className="programIncludes">Cardio Program</span>
                    )}
                    {item.workouts.has_stretch_workouts && (
                      <span className="programIncludes">Flexibility Program</span>
                    )}
                    {item.workouts.has_strength_workouts && (
                      <span className="programIncludes">Strength Program</span>
                    )}
                  </Grid>
                </Grid>
              </button>
            ))}
            {loading && (
              <Stack alignItems={"center"}>
                <CircularProgress disableShrink color="success" />
              </Stack>
            )}
          </Box>
        </Box>
      </Stack>
    </Modal>
  );
};

export default memo(CopyFromMemberModal);
